/**
 * KAN-858: вся переписка клиента в приложении менеджера. Сначала последние
 * MOBILE_FEED_PAGE сообщений, прокрутка вверх увеличивает ?limit= (backend
 * отдаёт последние limit, от старых к новым). Пока страница видна, лента
 * перечитывается раз в MOBILE_FEED_POLL_MS (react-query не опрашивает
 * скрытую вкладку), так ответы клиента появляются сами.
 * Ключ вложен в transcriptQueryKey(clientId, null): отправка, ручной режим,
 * "Вернуть боту" и касание бота обновляют и эту ленту.
 * KAN-929: calls - звонки менеджера клиента (useClientCalls, без ?limit),
 * лента ставит их между сообщениями; их ошибка не делает ленту isError.
 */
import { useCallback, useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { transcriptQueryKey } from '@/components/clients/useTranscriptMessages';
import { dashboardApi } from '@/services/api/dashboard';
import { useClientCalls } from '@/hooks/useClientCalls';
import { feedHasMore, MOBILE_FEED_PAGE, MOBILE_FEED_POLL_MS, nextFeedLimit } from '@/lib/mobileChat';
import type { ClientCall } from '@/types/clientCall';
import type { EstimateMessage } from '@/types/estimate';

export function mobileFeedQueryKey(clientId: string, limit: number): readonly unknown[] {
  return [...transcriptQueryKey(clientId, null), 'mobile', limit];
}

export interface MobileClientFeed {
  messages: EstimateMessage[];
  /** KAN-929: звонки менеджера клиента, от старых к новым. */
  calls: ClientCall[];
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  /** Идёт загрузка ранних сообщений (после loadMore). */
  isLoadingMore: boolean;
  loadMore: () => void;
}

const NO_MESSAGES: EstimateMessage[] = [];
/** KAN-929: звонки меняются реже сообщений, опрос раз в 30 с. */
const MOBILE_CALLS_POLL_MS = 30_000;

export function useMobileClientFeed(clientId: string): MobileClientFeed {
  const [limit, setLimit] = useState(MOBILE_FEED_PAGE);
  useEffect(() => setLimit(MOBILE_FEED_PAGE), [clientId]);
  const query = useQuery({
    queryKey: mobileFeedQueryKey(clientId, limit),
    queryFn: () => dashboardApi.getClientMessages(clientId, limit),
    enabled: !!clientId,
    placeholderData: keepPreviousData,
    refetchInterval: MOBILE_FEED_POLL_MS,
  });
  const calls = useClientCalls(clientId, { refetchInterval: MOBILE_CALLS_POLL_MS });
  const messages = query.data ?? NO_MESSAGES;
  // Пока висит прошлая страница (placeholder), про "ещё" судить рано.
  const hasMore = !query.isPlaceholderData && feedHasMore(messages.length, limit);
  const loadMore = useCallback(() => {
    if (hasMore) setLimit(nextFeedLimit);
  }, [hasMore]);
  return {
    messages,
    calls,
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore,
    isLoadingMore: query.isPlaceholderData && query.isFetching,
    loadMore,
  };
}
